import { useState, useEffect, useCallback } from "react";
import { BellRinging, Trash, EnvelopeSimple } from "@phosphor-icons/react";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import { formatPrice } from "@/lib/constants";
import { timeAgo } from "./helpers";

function criteriaText(s) {
  const parts = [];
  parts.push(s.regions?.length ? s.regions.join(", ") : "Any area");
  if (s.property_types?.length) parts.push(s.property_types.join(", "));
  if (s.min_bedrooms) parts.push(`${s.min_bedrooms}+ bd`);
  if (s.min_price || s.max_price) {
    parts.push(`${s.min_price ? formatPrice(s.min_price) : "$0"}–${s.max_price ? formatPrice(s.max_price) : "∞"}`);
  }
  return parts.join(" · ");
}

export default function Alerts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const { data } = await api.get("/saved-searches");
    setItems(data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (s) => {
    if (!window.confirm(`Remove alert subscription for ${s.email}?`)) return;
    await api.delete(`/saved-searches/${s.id}`);
    toast.success("Subscription removed");
    load();
  };

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div>
        <h1 className="text-3xl text-white">Listing Alerts</h1>
        <p className="text-white/50 text-sm mt-1">
          {items.length} buyer{items.length === 1 ? "" : "s"} get auto-notified when a new matching listing goes live.
        </p>
      </div>

      {loading ? (
        <p className="text-white/40 text-sm">Loading…</p>
      ) : items.length === 0 ? (
        <div className="bg-[#101116] border border-white/5 rounded-sm py-16 text-center">
          <BellRinging size={40} weight="duotone" className="mx-auto text-brand/50 mb-4" />
          <p className="text-white/50">No alert subscribers yet. They appear when buyers tap "Get alerts" on your listings page.</p>
        </div>
      ) : (
        <div className="bg-[#101116] border border-white/5 rounded-sm divide-y divide-white/5">
          {items.map((s) => (
            <div key={s.id} data-testid={`alert-row-${s.id}`} className="flex items-center justify-between gap-4 px-6 py-4">
              <div className="min-w-0">
                <p className="text-white">{s.name}</p>
                <p className="text-xs text-white/40">{s.email}{s.phone ? ` · ${s.phone}` : ""} · {timeAgo(s.created_at)}</p>
                <p className="text-xs text-brand mt-1">{criteriaText(s)}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a href={`mailto:${s.email}`} className="p-2 rounded-sm border border-white/10 text-white/60 hover:text-brand hover:border-brand/40 transition-colors" title="Email">
                  <EnvelopeSimple size={16} />
                </a>
                <button onClick={() => remove(s)} data-testid={`alert-delete-${s.id}`} className="p-2 rounded-sm border border-white/10 text-white/50 hover:text-red-400 hover:border-red-400/40 transition-colors" title="Remove">
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
